<template>
  <div>
    <page-header :title-in-breadcrumbs="titleInBreadcrumbs"/>
    <div class="public-container public-container-mobile">
      <locations-discount
        :ref-calculator="refCalculator"
        :url-direction="urlDirection"
      />

      <locations-calculator-wrapper>
        <vz-calculator ref="refCalculator" promocode="VEZET15" />
      </locations-calculator-wrapper>

      <public-client-button :has-icon="false"/>

      <div
        v-if="hasSchedule"
        class="run-schedule-block"
        id="run-schedule"
      >
        <run-schedule-directions
          title-h3
          hideScheduleCollapse
        />
      </div>

      <h3 class="vz-text-align-left mt-110 mb-30">Список видов перевозок и услуг</h3>
      <locations-cargo />

      <locations-offer class="mt-110-90-70-70"/>

      <h3 class="vz-text-align-left mt-110-90-70-70">
        Терминалы для отправки груза {{ from.name }} — {{ to.name }}
      </h3>
      <locations-contacts-terminals is-from/>
      <locations-contacts-terminals/>

      <popular-directions
        class="mt-110-90-70-70"
        two-location-editable
        :has-fast-order="true"
      />

      <locations-multi-pickup />

      <locations-links/>
    </div>
  </div>
</template>

<script setup lang="ts">
import PageHeader from "~/components/public/page-header.vue";
import VzCalculator from "~/components/public/order/vz-calculator.vue";
import {Location} from "~/types/types";
import {getCityByTranslit} from "~/api/city";
import {setYM} from "~/common/functions";
import {useCityStore} from "~/stores/city";
import {storeToRefs} from "pinia";
import RunScheduleDirections from "~/components/public/run-schedule/run-schedule-directions.vue";
import LocationsContactsTerminals from "~/components/public/locations/locations-contacts-terminals.vue";
import LocationsDiscount from "~/components/public/locations/locations-discount/locations-discount.vue";
import LocationsOffer from "~/components/public/locations/locations-offer/locations-offer.vue";
import PopularDirections from "~/components/public/popular-directions/popular-directions.vue";
import LocationsLinks from "~/components/public/locations/locations-links.vue";
import LocationsCargo from "~/components/public/locations/locations-cargo.vue"
import PublicClientButton from "~/components/public/public-client-button.vue";
import LocationsMultiPickup from "~/components/public/locations/locations-multi-pickup.vue"
import LocationsCalculatorWrapper from "~/components/public/locations/locations-calculator-wrapper.vue"
import {until} from "@vueuse/shared";
import {useTerminalStore} from "~/stores/terminal";
import {usePopularDirectionsStore} from "~/stores/popular-directions";
import {PublicLocationsTag} from "~/types/public";
import useMenuItems from "~/composables/useMenuItems";
import {getDirectionsDistance} from "~/api/directions";

const nuxtApp = useNuxtApp()
const {setTo, setFrom} = useCityStore()
const {oneLocation, twoLocation} = storeToRefs(usePopularDirectionsStore())
const {from, to, rclCityList} = storeToRefs(useCityStore())
const {terminalList} = storeToRefs(useTerminalStore())
const route = useRoute()
const refCalculator = ref()
const urlDirection = ref()

async function getAsyncData() {
  let newDomainCity = {} as Location | undefined
  let newCityDispatch = {} as Location | undefined
  let newCityDestination = {} as Location | undefined

  const {locationFrom, locationTo} = route.params as Record<string, string>
  if (!locationFrom || !locationTo) {
    showError({statusCode: 404})
    return;
  }

  if (locationFrom === locationTo) {
    navigateTo(`/order/create/${locationFrom}`)
  }

  try {
    const res = await getDirectionsDistance(`${locationFrom}_${locationTo}`)
    if(res[0]?.url) {
      urlDirection.value = res[0]
    }
  } catch (e) {
    console.log(e)
  }

  // isOneLocation todo
  const cityByTranslit = await getCityByTranslit({
    city: locationFrom,
    path: route.path,
    orderWay: `${locationFrom}_${locationTo}`,
    isOneLocation: false
  })

  if (cityByTranslit.redirect) {
    return nuxtApp.runWithContext(() => navigateTo(cityByTranslit.redirect, {redirectCode: 301}));
  }

  if (cityByTranslit.error) {
    showError({statusCode: 404})
    return;
  }

  if (cityByTranslit?.from) {
    oneLocation.value = cityByTranslit?.from
  }
  if (cityByTranslit?.to) {
    twoLocation.value = cityByTranslit?.to
  }

  if (from.value && locationFrom !== from.value.translit) {
    newCityDispatch = rclCityList.value.find(c => [c.abbr, c.translit].includes(locationFrom))
    newDomainCity = newCityDispatch

    //  если location не найден, то пробуем найти через getCityByTranslit from.rcl_guid
    if (!newDomainCity) {
      newDomainCity = rclCityList.value.find(c => c.guid === cityByTranslit.from.rcl_guid) as Location
    }
    if (!newDomainCity) {
      return
    }

    if (newCityDispatch) {
      setFrom(newCityDispatch)
    }
  }

  if (to.value && locationTo !== to.value.translit) {
    newCityDestination = rclCityList.value.find(c => [c.abbr, c.translit].includes(locationTo))
    if (newCityDestination) {
      setTo(newCityDestination)
    }
  }

  if (cityByTranslit?.from && !newCityDispatch) {
    setFrom(cityByTranslit.from)
  }
  if (cityByTranslit?.to && !newCityDestination) {
    setTo(cityByTranslit.to)
  }

  // seo - по Нижегородской области должны отрабатывать страницы только из Нижнего Новгорода или Дзержинска
  if (from.value && from.value.rcl_guid === '1d016bfc-0127-11e5-80c7-00155d903d03' &&
    from.value.guid !== '1d016bfd-0127-11e5-80c7-00155d903d03') {
    showError({statusCode: 404})
  }
}

const hasSchedule = computed(() => {
  return (from.value.rcl_guid || from.value.guid) !== (to.value.rcl_guid || to.value.guid)
})

const titleInBreadcrumbs = computed(() => {
  return `${from?.value?.name} - ${to?.value.name}`
})

onMounted(async () => {
  setYM(38058485, 'napravleniya')
})

await Promise.all([
  until(() => !!terminalList.value.length && !!rclCityList.value?.length).toBe(true),
  getAsyncData()
])
</script>

<style lang="scss" scoped>
.personal-transportation-link {
  display: flex;
  margin-bottom: 20px;
  align-items: center;

  &-text {
    font-size: 18px;
    line-height: 26px;
  }
}

.locations-description {
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: $pub-xs) {
    flex-direction: column;
  }

  :deep(.countries-card img) {
    width: 400px;

    @media screen and (max-width: $pub-md) {
      width: 336px;
    }

    @media screen and (max-width: $pub-sm) {
      width: 239px;
    }

    @media screen and (max-width: $pub-xs) {
      width: 304px;
    }
  }

  &-wrapper {
    margin-right: 20px;

    @media screen and (max-width: $pub-xs) {
      margin-right: 0;
    }
  }
}

.locations-description,
.run-schedule-block {
  margin-top: 110px !important;

  @media screen and (max-width: $pub-md) {
    margin-top: 90px !important;
  }

  @media screen and (max-width: $pub-sm) {
    margin-top: 70px !important;
  }
}

.locations-calculator-title {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: start;

  @media only screen and (max-width: $pub-xs) {
    flex-direction: column;
  }

  h2 {
    margin-bottom: 0;

    @media only screen and (max-width: $pub-xs) {
      margin-bottom: 10px;
    }
  }
}

.locations-calculator-time {
  display: flex;
  font-weight: bold;
  color: $light-green;
  align-items: center;
}

:deep(.shipping-types-list ) {
  background-color: transparent;
}

:deep(.public-client-button) {
  margin-left: auto;
  margin-right: auto;
  max-width: 264px;
  width: 100%;
  justify-content: center;

  @media screen and (max-width: $pub-xs) {
    max-width: none;
  }
}
</style>
